import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert,
  Badge,
} from "reactstrap";
import Swal from "sweetalert2";
import {
  getUserCustomPricing,
  updateUserCustomPricing,
  removeUserCustomPricing,
  getUserPricingHierarchy,
} from "../../../slices/admin/thunk";
import { formatCentsToDollars } from "../../../utils/utils";
import { DASHBOARD_USER_ROLES } from "../../../constants";

const UserCustomPricingModal = ({ isOpen, setIsOpen, userProfile, currentUser, onRefresh }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);

  // Admin access validation
  const isCurrentUserAdmin = currentUser?.role === DASHBOARD_USER_ROLES.ADMIN;

  // Current pricing state
  const [currentPricing, setCurrentPricing] = useState(null);
  const [pricingHierarchy, setPricingHierarchy] = useState(null);

  // Form states
  const [individualAmount, setIndividualAmount] = useState("");
  const [familyAmount, setFamilyAmount] = useState("");

  // Validation states
  const [individualError, setIndividualError] = useState("");
  const [familyError, setFamilyError] = useState("");
  const [formError, setFormError] = useState("");

  const resetModal = () => {
    setCurrentPricing(null);
    setPricingHierarchy(null);
    setIndividualAmount("");
    setFamilyAmount("");
    setIndividualError("");
    setFamilyError("");
    setFormError("");
  };

  const handleClose = () => {
    resetModal();
    setIsOpen(false);
  };

  const fetchPricingData = async () => {
    if (!userProfile?.id) return;

    try {
      setFetchingData(true);

      // Fetch current custom pricing
      const pricingResponse = await dispatch(getUserCustomPricing({ userId: userProfile.id }));
      if (pricingResponse.payload?.data) {
        setCurrentPricing(pricingResponse.payload.data);

        // Pre-fill form if custom pricing exists
        if (pricingResponse.payload.data.customPricing) {
          const pricing = pricingResponse.payload.data.customPricing;
          setIndividualAmount(pricing.individualPlanAmount.toString());
          setFamilyAmount(pricing.familyPlanAmount.toString());
        }
      }

      // Fetch pricing hierarchy for debugging/context
      const hierarchyResponse = await dispatch(getUserPricingHierarchy({ userId: userProfile.id }));
      if (hierarchyResponse.payload?.data) {
        setPricingHierarchy(hierarchyResponse.payload.data);
      }
    } catch (error) {
      console.error("Error fetching pricing data:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to load pricing data",
        icon: "error",
      });
    } finally {
      setFetchingData(false);
    }
  };

  useEffect(() => {
    if (isOpen && userProfile) {
      fetchPricingData();
      resetModal();
    }
  }, [isOpen, userProfile]);

  // Get user display name
  const userName = userProfile ? (
    userProfile.role === DASHBOARD_USER_ROLES.BROKER
      ? `${userProfile.firstName || ""} ${userProfile.lastName || ""}`.trim()
      : userProfile.companyName || `${userProfile.firstName || ""} ${userProfile.lastName || ""}`.trim()
  ) : "";

  const validateForm = () => {
    let isValid = true;
    setIndividualError("");
    setFamilyError("");
    setFormError("");

    const individual = parseInt(individualAmount);
    const family = parseInt(familyAmount);

    // Validate individual amount (in cents)
    if (!individualAmount || isNaN(individual) || individual < 100 || individual > 10000) {
      setIndividualError("Individual amount must be between 100 and 10000 cents ($1.00 - $100.00)");
      isValid = false;
    }

    // Validate family amount (in cents)
    if (!familyAmount || isNaN(family) || family < 100 || family > 15000) {
      setFamilyError("Family amount must be between 100 and 15000 cents ($1.00 - $150.00)");
      isValid = false;
    }

    // Validate relationship between amounts
    if (isValid && family < individual) {
      setFormError("Family amount must be greater than or equal to individual amount");
      isValid = false;
    }

    return isValid;
  };

  const handleUpdatePricing = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      // Values are already in cents
      const individualCents = parseInt(individualAmount);
      const familyCents = parseInt(familyAmount);

      await dispatch(updateUserCustomPricing({
        userId: userProfile.id,
        individualPlanAmount: individualCents,
        familyPlanAmount: familyCents,
        allowPricingOverride: true,
      }));

      Swal.fire({
        title: "Success!",
        text: "Custom pricing updated successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      // Refresh data
      await fetchPricingData();
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error("Error updating custom pricing:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update custom pricing",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePricing = async () => {
    const result = await Swal.fire({
      title: "Remove Custom Pricing?",
      text: "This will remove all custom pricing for this user and revert to default pricing.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);

      await dispatch(removeUserCustomPricing({ userId: userProfile.id }));

      Swal.fire({
        title: "Removed!",
        text: "Custom pricing has been removed successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      // Reset form and refresh data
      setIndividualAmount("");
      setFamilyAmount("");
      await fetchPricingData();
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error("Error removing custom pricing:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to remove custom pricing",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case DASHBOARD_USER_ROLES.NATIONAL:
        return <Badge color="primary" pill>National</Badge>;
      case DASHBOARD_USER_ROLES.GA:
        return <Badge color="info" pill>GA</Badge>;
      case DASHBOARD_USER_ROLES.BROKER:
        return <Badge color="success" pill>Broker</Badge>;
      case DASHBOARD_USER_ROLES.EMPLOYER:
        return <Badge color="warning" pill>Employer</Badge>;
      case DASHBOARD_USER_ROLES.ADMIN:
        return <Badge color="danger" pill>Admin</Badge>;
      default:
        return <Badge color="secondary" pill>{role}</Badge>;
    }
  };

  // Security check: Only allow admin users
  if (!isCurrentUserAdmin) {
    return null;
  }

  if (!userProfile) {
    return null;
  }

  const hasCustomPricing = currentPricing?.hasCustomPricing;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        Custom Pricing for {userName}
      </ModalHeader>
      <ModalBody>
        {fetchingData ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2">Loading pricing data...</p>
          </div>
        ) : (
          <>
            <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <CardHeader>
                    <h6 className="mb-0">User Information</h6>
                  </CardHeader>
                  <CardBody>
                    <p><strong>Name:</strong> {userName}</p>
                    <p><strong>Email:</strong> {userProfile.email}</p>
                    <p><strong>Role:</strong> {getRoleIcon(userProfile.role)}</p>
                    <p>
                      <strong>Custom Pricing Status:</strong>
                      <span className={`ms-2 badge ${hasCustomPricing ? 'bg-success' : 'bg-secondary'}`}>
                        {hasCustomPricing ? 'Enabled' : 'Default'}
                      </span>
                    </p>
                  </CardBody>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="mb-3">
                  <CardHeader>
                    <h6 className="mb-0">Current Pricing</h6>
                  </CardHeader>
                  <CardBody>
                    {hasCustomPricing ? (
                      <div>
                        <p>
                          <strong>Individual Plan:</strong>
                          <span className="ms-2">{currentPricing.customPricing.individualPlanAmount} cents (${formatCentsToDollars(currentPricing.customPricing.individualPlanAmount)})</span>
                        </p>
                        <p>
                          <strong>Family Plan:</strong>
                          <span className="ms-2">{currentPricing.customPricing.familyPlanAmount} cents (${formatCentsToDollars(currentPricing.customPricing.familyPlanAmount)})</span>
                        </p>
                      </div>
                    ) : (
                      <p className="text-muted">Using default pricing structure</p>
                    )}

                    {pricingHierarchy?.hierarchicalPricing && (
                      <div className="mt-3 pt-3 border-top">
                        <h6 className="text-muted mb-2">Hierarchical Pricing</h6>
                        <p className="small">
                          <strong>Source:</strong> {pricingHierarchy.hierarchicalPricing.source}
                        </p>
                        <p className="small">
                          <strong>Individual:</strong> {pricingHierarchy.hierarchicalPricing.singleEmployeeCost} cents (${formatCentsToDollars(pricingHierarchy.hierarchicalPricing.singleEmployeeCost)})
                        </p>
                        <p className="small">
                          <strong>Family:</strong> {pricingHierarchy.hierarchicalPricing.familyEmployeeCost} cents (${formatCentsToDollars(pricingHierarchy.hierarchicalPricing.familyEmployeeCost)})
                        </p>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Card>
                  <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">
                        {hasCustomPricing ? 'Update Custom Pricing' : 'Set Custom Pricing'}
                      </h6>
                      {hasCustomPricing && (
                        <Button
                          color="danger"
                          size="sm"
                          onClick={handleRemovePricing}
                          disabled={loading}
                        >
                          <i className="ri-delete-bin-line me-1"></i>
                          Remove Custom Pricing
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardBody>
                    <Form>
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Individual Plan Amount (cents) *</Label>
                            <Input
                              type="number"
                              step="1"
                              min="100"
                              max="10000"
                              value={individualAmount}
                              onChange={(e) => {
                                setIndividualAmount(e.target.value);
                                if (individualError) setIndividualError("");
                                if (formError) setFormError("");
                              }}
                              invalid={!!individualError}
                              placeholder="1500"
                            />
                            {individualError && <div className="invalid-feedback">{individualError}</div>}
                            <small className="text-muted">
                              Range: 100 - 10000 cents ($1.00 - $100.00). Example: 1500 = $15.00
                            </small>
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Family Plan Amount (cents) *</Label>
                            <Input
                              type="number"
                              step="1"
                              min="100"
                              max="15000"
                              value={familyAmount}
                              onChange={(e) => {
                                setFamilyAmount(e.target.value);
                                if (familyError) setFamilyError("");
                                if (formError) setFormError("");
                              }}
                              invalid={!!familyError}
                              placeholder="2200"
                            />
                            {familyError && <div className="invalid-feedback">{familyError}</div>}
                            <small className="text-muted">
                              Range: 100 - 15000 cents ($1.00 - $150.00). Must be ≥ individual amount. Example: 2200 = $22.00
                            </small>
                          </FormGroup>
                        </Col>
                      </Row>


                      {formError && (
                        <Alert color="danger" className="py-2 mt-3">
                          {formError}
                        </Alert>
                      )}

                      <div className="mt-4">
                        <Button
                          color="primary"
                          onClick={handleUpdatePricing}
                          disabled={loading || !individualAmount || !familyAmount}
                          block
                        >
                          {loading ? <Spinner size="sm" className="me-1" /> : null}
                          {hasCustomPricing ? 'Update Custom Pricing' : 'Set Custom Pricing'}
                        </Button>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={loading}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default UserCustomPricingModal;